import { CSS } from '@stitches/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { COMMON_TRANSLATION } from '@constants/index';
import { PercentageBarItem, PercentageBarProps, PercentageItemTextFormatter } from './PercentageBar.types';
import { StyledPercentageBar, StyledPercentageItem } from './PercentageBar.styles';
import { Box } from '@base-app';

const PercentageBar: FC<PercentageBarProps> = ({ isDataAvailable = true, items, percentageItemTextFormatter }) => {
  const total: number = items.map((item) => item.value).reduce((prev, curr) => curr + prev);

  const defaultPercentageItemTextFormatter: PercentageItemTextFormatter = (
    text: string,
    value: number,
    percentage: string,
    _total: number,
  ) => {
    return `${text}: ${value}, ${percentage}`;
  };

  const barItems = items.map(({ text, value, textColor, bgColor }: PercentageBarItem, index) => {
    const { t } = useTranslation(COMMON_TRANSLATION);

    const percentage = (value / total) * 100;

    // Remove unnecessary zeros if exists.
    const displayPercentage = isDataAvailable ? `${Number(Number(percentage).toFixed(2))}%` : '50%';

    const getDataAvailableText = (): string | undefined => {
      if (value === 0) {
        return;
      }

      return percentageItemTextFormatter
        ? percentageItemTextFormatter(text, value, displayPercentage, total)
        : defaultPercentageItemTextFormatter(text, value, displayPercentage, total);
    };

    const getTextPosition: any = () => {
      const MIN_VALUE = 15;
      const css: CSS = { position: 'absolute', zIndex: '1', whiteSpace: 'nowrap' };

      if ((percentage <= MIN_VALUE && isDataAvailable) || !isDataAvailable) {
        if (index === items.length - 1) {
          return { ...css, right: '$3' };
        } else if (index === 0) {
          return { ...css, left: '$3' };
        }
      }
      return { ...css, left: '50%', transform: 'translateX(-50%)' };
    };

    return (
      <StyledPercentageItem
        data-testid={`percentage-bar-item-${index}`}
        key={index}
        first={index === 0}
        last={index === items.length - 1}
        css={{ backgroundColor: bgColor, color: textColor, width: displayPercentage, position: 'relative' }}
      >
        {isDataAvailable ? (
          <Box data-testid={`percentage-bar-item-text-${index}`} css={{ ...getTextPosition() }}>
            {getDataAvailableText()}
          </Box>
        ) : (
          <Box data-testid={`percentage-bar-item-text-${index}`} css={{ ...getTextPosition() }}>{`${text}: ${t(
            'no_data_available',
          )}`}</Box>
        )}
      </StyledPercentageItem>
    );
  });

  return <StyledPercentageBar data-testid="percentage-bar">{barItems}</StyledPercentageBar>;
};

export default PercentageBar;
